import React from 'react'
import Image from 'next/image'
import NextLink from 'next/link'
import { useRouter } from 'next/router'
import clsx from 'clsx'

import { Button } from '@wartek-id/button-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import type { ButtonProps } from '@wartek-id/button-v1'
import type { DialogProps } from '@wartek-id/dialog-v1'

import PelaksanaanPraktikKinerjaStatusBadge from 'app/KinerjaPenilikPegawaiPage/components/PelaksanaanPraktikKinerjaStatusBadge'
import { usePenilikTrackEvent } from 'app/KinerjaPenilikPegawaiPage/hooks/usePenilikTrackEvent'

import type { StatusPelaksanaanPraktikKinerja } from 'types/kinerja-penilik-pegawai'

const DialogConfirmation = ({
  title,
  description,
  imageSrc,
  ctaConfirm,
  onBack,
  onConfirm,
  ...dialogProps
}: Omit<DialogProps, 'children'> & {
  title: string
  description: string | React.ReactNode
  imageSrc: string
  ctaConfirm: string
  onBack?: () => void
  onConfirm: () => void
}) => {
  return (
    <Dialog {...dialogProps} autoFocus={false}>
      <DialogOverlay />
      <DialogContent>
        <div className="p-6 space-y-6">
          <div>
            <Image alt={title} src={imageSrc} height="84px" width="84px" />
          </div>

          <div className="space-y-3">
            <Text variant="heading-lg">{title}</Text>
            <Text>{description}</Text>
          </div>

          <div className="flex gap-3">
            <Button size="lg" color="white" onClick={onBack}>
              Kembali
            </Button>
            <Button size="lg" color="blue" onClick={onConfirm}>
              {ctaConfirm}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface PelaksanaanPraktikKinerjaCardLinkProps
  extends React.HTMLAttributes<HTMLDivElement> {
  href: string
}

const PelaksanaanPraktikKinerjaCardLink = ({
  href,
  children,
}: PelaksanaanPraktikKinerjaCardLinkProps) => {
  if (Boolean(href)) {
    return (
      <NextLink href={href} passHref>
        {children}
      </NextLink>
    )
  }

  return <>{children}</>
}

interface PelaksanaanPraktikKinerjaCardProps
  extends React.HTMLAttributes<HTMLDivElement> {
  imageSrc: string
  title: string
  currentState: StatusPelaksanaanPraktikKinerja
  states: StatusPelaksanaanPraktikKinerja[]
  href?: string
  disabled?: boolean
  done?: boolean
  appeal?: boolean
  onCtaClick?: () => void
}

const BUTTON_STATES: Record<
  StatusPelaksanaanPraktikKinerja,
  { text: string; color: ButtonProps['color'] }
> = {
  DISKUSI_PERSIAPAN: { text: 'Isi', color: 'blue' },
  PENDAMPINGAN_SEKOLAH: { text: 'Isi', color: 'blue' },
  DISKUSI_TINDAK_LANJUT: { text: 'Cek panduan', color: 'blue' },
  DISKUSI_TINDAK_LANJUT_PENILAIAN: { text: 'Cek', color: 'white' },
  UPAYA_TINDAK_LANJUT: { text: 'Isi', color: 'blue' },
  REFLEKSI_TINDAK_LANJUT: { text: 'Isi', color: 'blue' },
  REFLEKSI_TINDAK_LANJUT_PENILAIAN: { text: 'Cek', color: 'white' },
  SELESAI: { text: 'Cek', color: 'white' },
}

const POPUP_STATES: Partial<
  Record<
    StatusPelaksanaanPraktikKinerja,
    {
      title: string
      imageSrc: string
      description: string | React.ReactNode
      ctaConfirm: string
      trackerID: string
    }
  >
> = {
  DISKUSI_PERSIAPAN: {
    trackerID: 'A',
    title: 'Isi sembari diskusi bersama Tim Kinerja',
    imageSrc: '/illustrations/img-document-penci-fill.png',
    description:
      'Sehingga Anda dapat mempersiapkan Pendampingan Satuan Pendidikan dengan lebih matang.',
    ctaConfirm: 'Lanjut isi',
  },
  PENDAMPINGAN_SEKOLAH: {
    trackerID: 'B',
    title: 'Isi sembari pendampingan (1-3 bulan)',
    imageSrc: '/illustrations/img-document-interaction-discussion.png',
    description: (
      <ul className="pl-6 list-disc">
        <li>Pengisian Anda menjadi topik untuk Diskusi Tindak Lanjut.</li>
        <li>
          Terdapat survei Kepala Satuan Pendidikan Nonformal yang akan
          dibagikan. Survei menjadi pertimbangan Tim Kinerja ketika memberikan
          hasil pemantauan.
        </li>
      </ul>
    ),
    ctaConfirm: 'Lanjut isi',
  },
  DISKUSI_TINDAK_LANJUT: {
    trackerID: 'C',
    title:
      'Proses diskusi akan diberikan hasil pemantauan. Berikut panduannya:',
    imageSrc: '/illustrations/img-chat-talk.png',
    description: (
      <ul className="pl-6 list-disc">
        <li>
          Diskusikan pengisian pendampingan bersama Tim Kinerja. Sehingga Tim
          Kinerja memiliki pemahaman yang cukup untuk memberikan hasil
          pemantauan.
        </li>
        <li>
          Tim Kinerja akan memberikan hasil pemantauan dengan mempertimbangkan
          isi catatan pendampingan dan survei Kepala Satuan Pendidikan
          Nonformal.
        </li>
      </ul>
    ),
    ctaConfirm: 'Lanjut cek pengisian pendampingan',
  },
  UPAYA_TINDAK_LANJUT: {
    trackerID: 'D',
    title: 'Isi realisasi upaya yang sudah Anda lakukan',
    imageSrc: '/illustrations/img-chalk-board-teach-character-stundet.png',
    description: (
      <ul className="pl-6 list-disc">
        <li>
          Upaya Tindak Lanjut idealnya dilakukan selama kurang lebih 1 bulan.
        </li>
        <li>Setelah melakukan upaya, catat apa saja yang sudah dilakukan.</li>
      </ul>
    ),
    ctaConfirm: 'Lanjut isi',
  },
  REFLEKSI_TINDAK_LANJUT: {
    trackerID: 'E',
    title: 'Pengisian akan diberikan hasil pemantauan',
    imageSrc: '/illustrations/img-book-rapor-graphic.png',
    description: 'Refleksikan Upaya Tindak Lanjut yang sudah Anda lakukan.',
    ctaConfirm: 'Lanjut isi',
  },
}

export default function PelaksanaanPraktikKinerjaCard({
  imageSrc,
  title,
  states,
  currentState,
  appeal,
  href,
  disabled = false,
  done = false,
  className,
  ...props
}: PelaksanaanPraktikKinerjaCardProps) {
  const router = useRouter()
  const { trackEvent } = usePenilikTrackEvent()

  const [isDialogOpen, setDialogOpen] = React.useState<boolean>(false)

  let buttonProps = states.includes(currentState)
    ? BUTTON_STATES[currentState]
    : { text: 'Cek', color: 'white' as ButtonProps['color'] }
  let popupProps = states.includes(currentState)
    ? POPUP_STATES[currentState]
    : null

  if (appeal) {
    buttonProps = {
      text: 'Cek',
      color: 'white' as ButtonProps['color'],
    }
    popupProps = null
  }

  return (
    <div
      className={clsx(
        'px-6 py-4 bg-white border rounded-md border-subdued flex gap-6 border-b-subdued',
        disabled && 'grayscale !bg-gray-20',
        className
      )}
      {...props}
    >
      <Image src={imageSrc} width={64} height={64} alt="" />
      <div className="flex items-center justify-between flex-1 gap-1">
        <div className="flex flex-col gap-3">
          <Text variant="heading-sm">{title}</Text>
          <PelaksanaanPraktikKinerjaStatusBadge
            state={states.includes(currentState) ? currentState : states[0]}
            done={done}
            disabled={disabled}
            appeal={appeal}
          />
        </div>
        <div className="ml-auto">
          {disabled ? (
            <Icon color="disabled" fontSize="lg">
              lock_outline
            </Icon>
          ) : (
            <PelaksanaanPraktikKinerjaCardLink {...(!popupProps && { href })}>
              <Button
                data-testid="pelaksanaan-praktik-kinerja-card-cta"
                size="lg"
                color={buttonProps.color as ButtonProps['color']}
                onClick={() => {
                  if (popupProps) {
                    setDialogOpen(true)
                  }
                }}
              >
                {buttonProps.text}
              </Button>
            </PelaksanaanPraktikKinerjaCardLink>
          )}
        </div>
      </div>

      <DialogConfirmation
        {...popupProps}
        isOpen={isDialogOpen}
        onConfirm={() => {
          trackEvent(
            'pelaksanaan pegawai',
            `start form ${popupProps.trackerID}`
          )
          setDialogOpen(false)
          router.push(href)
        }}
        onBack={() => setDialogOpen(false)}
      />
    </div>
  )
}
